<script setup>
import { ref } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import SupplierPortalLayout from '@/Layouts/SupplierPortalLayout.vue'
import AnonymizedRentalRow from '@/Components/SupplierPortal/AnonymizedRentalRow.vue'
import OffFleetModal from '@/Components/SupplierPortal/OffFleetModal.vue'
import PutOnMaintenanceModal from '@/Components/SupplierPortal/PutOnMaintenanceModal.vue'
import CloseMaintenanceModal from '@/Components/SupplierPortal/CloseMaintenanceModal.vue'

defineOptions({ layout: SupplierPortalLayout })

const { t } = useI18n()
const props = defineProps({ vehicle: { type: Object, required: true } })
const activeIdx = ref(0)
const photos = props.vehicle.photo_urls?.length ? props.vehicle.photo_urls : []
const showModal = ref(false)
const showMaintOpen  = ref(false)
const showMaintClose = ref(false)

function clearBlock(b) {
  if (!b.owned_by_me) return
  if (!confirm(t('supplier_portal.vehicle.clear_confirm'))) return
  router.delete(`/vehicles/${props.vehicle.id}/off_fleet/${b.id}`)
}
</script>

<template>
  <Link href="/" class="back">← {{ t('supplier_portal.vehicle.back') }}</Link>
  <div class="title-row">
    <h1 class="title">
      {{ vehicle.make }} {{ vehicle.model }}
      <span v-if="vehicle.year" class="year">({{ vehicle.year }})</span>
    </h1>
    <Link :href="`/vehicles/${vehicle.id}/edit`" class="edit-btn">
      {{ t('supplier_portal.vehicle.edit') }}
    </Link>
  </div>

  <div class="layout">
    <!-- Gallery column -->
    <div class="gallery">
      <div class="gallery__main">
        <img
          v-if="photos[activeIdx]"
          :src="photos[activeIdx]"
          :alt="`${vehicle.make} ${vehicle.model}`"
        />
        <div v-else class="gallery__placeholder">
          {{ vehicle.make?.[0] }}{{ vehicle.model?.[0] }}
        </div>
      </div>
      <ul v-if="photos.length > 1" class="thumbs">
        <li v-for="(p, i) in photos" :key="p">
          <button
            :class="['thumb', i === activeIdx && 'is-active']"
            @click="activeIdx = i"
          >
            <img :src="p" alt="" />
          </button>
        </li>
      </ul>
    </div>

    <!-- Info cards column -->
    <div class="info">
      <!-- Sub-rental periods -->
      <section class="card">
        <h2>{{ t('supplier_portal.vehicle.sections.sub_rentals') }}</h2>
        <table class="tbl">
          <thead>
            <tr>
              <th>{{ t('supplier_portal.vehicle.cols.dates') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.rate') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.planned') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.paid') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.balance') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in vehicle.sub_rentals" :key="s.id">
              <td>{{ s.start_on }} → {{ s.end_on || t('supplier_portal.vehicle.open') }}</td>
              <td>{{ s.rate }}</td>
              <td>{{ s.planned || '—' }}</td>
              <td class="fw-value">{{ s.paid }}</td>
              <td class="fw-value">{{ s.balance }}</td>
            </tr>
            <tr v-if="vehicle.sub_rentals.length === 0">
              <td colspan="5" class="empty">{{ t('supplier_portal.vehicle.no_sub_rentals') }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Supplier payments -->
      <section class="card">
        <h2>{{ t('supplier_portal.vehicle.sections.payments') }}</h2>
        <table class="tbl">
          <thead>
            <tr>
              <th>{{ t('supplier_portal.vehicle.cols.date') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.method') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="s in vehicle.sub_rentals" :key="s.id">
              <tr v-for="p in s.payments" :key="p.id">
                <td>{{ p.paid_on }}</td>
                <td>{{ p.method }}</td>
                <td class="fw-value">{{ p.amount }}</td>
              </tr>
            </template>
            <tr v-if="vehicle.sub_rentals.every(s => !s.payments.length)">
              <td colspan="3" class="empty">{{ t('supplier_portal.vehicle.no_payments') }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Rental history (anonymized) -->
      <section class="card">
        <h2>{{ t('supplier_portal.vehicle.sections.history') }}</h2>
        <table class="tbl">
          <thead>
            <tr>
              <th>{{ t('supplier_portal.vehicle.cols.dates') }}</th>
              <th>{{ t('supplier_portal.vehicle.cols.status') }}</th>
            </tr>
          </thead>
          <tbody>
            <AnonymizedRentalRow
              v-for="r in vehicle.rentals"
              :key="r.id"
              :rental="r"
            />
            <tr v-if="vehicle.rentals.length === 0">
              <td colspan="2" class="empty">{{ t('supplier_portal.vehicle.no_history') }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Maintenance -->
      <section class="card">
        <div class="card__header">
          <h2>{{ t('supplier_portal.vehicle.sections.maintenance') }}</h2>
          <button
            v-if="!vehicle.open_maintenance"
            class="btn"
            @click="showMaintOpen = true"
          >
            {{ t('supplier_portal.vehicle.maintenance.put_cta') }}
          </button>
        </div>

        <div v-if="vehicle.open_maintenance" class="maint-open">
          <p class="maint-status">
            <strong>{{ t('supplier_portal.vehicle.maintenance.in_progress') }}</strong>
            · {{ vehicle.open_maintenance.description }}
          </p>
          <p class="muted">
            {{ t('supplier_portal.vehicle.maintenance.since') }} {{ vehicle.open_maintenance.started_at?.slice(0, 10) }}
            <span v-if="vehicle.open_maintenance.planned_end_date">
              · {{ t('supplier_portal.vehicle.maintenance.planned_back') }} {{ vehicle.open_maintenance.planned_end_date }}
            </span>
            <span v-if="vehicle.open_maintenance.started_by"> · {{ vehicle.open_maintenance.started_by }}</span>
          </p>
          <button class="btn" @click="showMaintClose = true">
            {{ t('supplier_portal.vehicle.maintenance.close_cta') }}
          </button>
        </div>

        <table v-if="vehicle.maintenance_history.length" class="tbl">
          <thead>
            <tr>
              <th>{{ t('supplier_portal.vehicle.cols.dates') }}</th>
              <th>{{ t('supplier_portal.vehicle.maintenance.description') }}</th>
              <th>{{ t('supplier_portal.vehicle.maintenance.actor') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in vehicle.maintenance_history" :key="log.id">
              <td>{{ log.started_at?.slice(0, 10) }} → {{ log.ended_at?.slice(0, 10) }}</td>
              <td>
                {{ log.description }}
                <div v-if="log.notes" class="muted">{{ log.notes }}</div>
              </td>
              <td>{{ log.closed_by || log.started_by }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else-if="!vehicle.open_maintenance" class="empty">
          {{ t('supplier_portal.vehicle.maintenance.empty') }}
        </p>

        <PutOnMaintenanceModal v-if="showMaintOpen" :vehicle="vehicle" @close="showMaintOpen = false" />
        <CloseMaintenanceModal v-if="showMaintClose && vehicle.open_maintenance" :vehicle="vehicle" :log="vehicle.open_maintenance" @close="showMaintClose = false" />
      </section>

      <!-- Off-fleet windows -->
      <section class="card">
        <div class="card__header">
          <h2>{{ t('supplier_portal.vehicle.sections.blocks') }}</h2>
          <button class="btn" @click="showModal = true">{{ t('supplier_portal.vehicle.block_cta') }}</button>
        </div>
        <ul class="blocks">
          <li v-for="b in vehicle.supplier_blocks" :key="b.id" class="block-row">
            {{ b.effective_start_on }} → {{ b.end_on }}
            <span class="muted" v-if="b.cleared_at">· {{ t('supplier_portal.vehicle.cleared') }}</span>
            <span class="muted" v-else-if="b.effective_start_on !== b.requested_start_on">· {{ t('supplier_portal.vehicle.queued') }}</span>
            <button v-if="b.owned_by_me && !b.cleared_at" class="link" @click="clearBlock(b)">{{ t('supplier_portal.vehicle.clear') }}</button>
          </li>
          <li v-if="vehicle.supplier_blocks.length === 0" class="empty">{{ t('supplier_portal.vehicle.no_blocks') }}</li>
        </ul>
        <OffFleetModal v-if="showModal" :vehicle="vehicle" @close="showModal = false" />
      </section>
    </div>
  </div>
</template>

<style scoped>
.back {
  color: var(--muted);
  text-decoration: none;
  font-size: 0.875rem;
}
.title-row {
  display: flex; align-items: center; gap: 1rem;
  margin: 0.5rem 0 1.25rem;
}
.title {
  font-family: var(--font-display, var(--display));
  font-size: 1.5rem;
  color: var(--ink);
  margin: 0;
  flex: 1;
}
.edit-btn {
  padding: 0.4rem 0.85rem;
  border: 1px solid var(--line); border-radius: var(--radius-button, 8px);
  color: var(--ink); text-decoration: none; font-size: 0.875rem; font-weight: 500;
}
.edit-btn:hover { border-color: var(--accent); color: var(--accent); }
.year {
  color: var(--muted);
  font-weight: 400;
}

/* Two-column layout on wider screens */
.layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 880px) {
  .layout {
    grid-template-columns: 1.1fr 1fr;
    gap: 2rem;
  }
}

/* Gallery */
.gallery__main {
  aspect-ratio: 4 / 3;
  background: var(--surfaceAlt);
  border-radius: 0.75rem;
  overflow: hidden;
  display: grid;
  place-items: center;
}
.gallery__main img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.gallery__placeholder {
  font-size: 4rem;
  font-weight: 700;
  color: var(--muted);
}
.thumbs {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
}
.thumb {
  width: 4.5rem;
  height: 3.5rem;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  background: var(--surfaceAlt);
}
.thumb.is-active {
  border-color: var(--accent);
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Info cards */
.info {
  display: grid;
  gap: 1rem;
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
}
.card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.card h2 {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  margin: 0 0 0.5rem;
}
.btn {
  padding: 0.35rem 0.7rem;
  border: 1px solid var(--line);
  background: var(--surfaceAlt);
  color: var(--ink);
  border-radius: var(--radius-button, 8px);
  cursor: pointer;
  font-size: 0.8125rem;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Table */
.tbl {
  width: 100%;
  border-collapse: collapse;
}
.tbl th,
.tbl td {
  padding: 0.4rem;
  text-align: left;
  border-bottom: 1px solid var(--line);
  font-size: 0.875rem;
}
.tbl th {
  font-size: 0.6875rem;
  color: var(--muted);
  text-transform: uppercase;
}
.empty {
  color: var(--muted);
  text-align: center;
  padding: 1rem;
}

/* Off-fleet blocks */
.blocks {
  list-style: none;
  padding: 0;
  margin: 0;
}
.block-row {
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--line);
  font-size: 0.875rem;
}
.block-row:last-child {
  border-bottom: 0;
}
.muted {
  color: var(--muted);
  font-size: 0.8125rem;
  margin-left: 0.4rem;
}
.link {
  background: none;
  border: 0;
  color: var(--accent);
  cursor: pointer;
  font-size: 0.8125rem;
  margin-left: 0.5rem;
}

/* Maintenance */
.maint-open {
  padding: 0.6rem 0.75rem;
  margin: 0 0 0.75rem;
  background: color-mix(in oklch, var(--warn) 14%, var(--surface));
  border-left: 3px solid var(--warn);
  border-radius: 0.5rem;
}
.maint-status { margin: 0 0 0.25rem; font-size: 0.875rem; color: var(--ink); }
.maint-open .muted { display: block; margin: 0 0 0.5rem; }
.maint-open .btn { background: var(--warn); border-color: var(--warn); color: white; }
</style>
